import type { ContractInstanceStatus, SignerStatus } from "@/lib/sign/signApiClient";

const INSTANCE_CHIP: Record<
  ContractInstanceStatus,
  { label: string; className: string; title: string }
> = {
  draft: {
    label: "draft",
    title: "Prefilled but not sent to signers yet.",
    className:
      "border-gray-200 bg-gray-50 text-gray-700 dark:border-zinc-600 dark:bg-zinc-800 dark:text-zinc-300",
  },
  sent: {
    label: "sent",
    title: "Envelope dispatched to signers.",
    className:
      "border-blue-200 bg-blue-50 text-blue-800 dark:border-blue-900 dark:bg-blue-950/35 dark:text-blue-200",
  },
  viewed: {
    label: "viewed",
    title: "At least one signer opened the document.",
    className:
      "border-indigo-200 bg-indigo-50 text-indigo-800 dark:border-indigo-900 dark:bg-indigo-950/35 dark:text-indigo-200",
  },
  partially_signed: {
    label: "partially signed",
    title: "Some signers completed; waiting on others.",
    className:
      "border-amber-200 bg-amber-50 text-amber-800 dark:border-amber-900 dark:bg-amber-950/35 dark:text-amber-200",
  },
  signed: {
    label: "signed",
    title: "All required signatures captured.",
    className:
      "border-emerald-200 bg-emerald-50 text-emerald-700 dark:border-emerald-900 dark:bg-emerald-950/35 dark:text-emerald-200",
  },
  declined: {
    label: "declined",
    title: "A signer refused to sign.",
    className:
      "border-red-200 bg-red-50 text-red-800 dark:border-red-900 dark:bg-red-950/35 dark:text-red-200",
  },
  expired: {
    label: "expired",
    title: "Signing window elapsed at the provider.",
    className:
      "border-orange-200 bg-orange-50 text-orange-800 dark:border-orange-900 dark:bg-orange-950/35 dark:text-orange-200",
  },
  voided: {
    label: "voided",
    title: "Cancelled by a manager or admin.",
    className:
      "border-gray-300 bg-gray-100 text-gray-600 dark:border-zinc-600 dark:bg-zinc-800/80 dark:text-zinc-400",
  },
};

const SIGNER_CHIP: Record<
  SignerStatus,
  { label: string; className: string }
> = {
  pending: {
    label: "pending",
    className:
      "border-gray-200 bg-gray-50 text-gray-600 dark:border-zinc-700 dark:bg-zinc-800 dark:text-zinc-400",
  },
  sent: {
    label: "sent",
    className:
      "border-blue-200 bg-blue-50 text-blue-700 dark:border-blue-900 dark:bg-blue-950/35 dark:text-blue-200",
  },
  viewed: {
    label: "viewed",
    className:
      "border-indigo-200 bg-indigo-50 text-indigo-700 dark:border-indigo-900 dark:bg-indigo-950/35 dark:text-indigo-200",
  },
  signed: {
    label: "signed",
    className:
      "border-emerald-200 bg-emerald-50 text-emerald-700 dark:border-emerald-900 dark:bg-emerald-950/35 dark:text-emerald-200",
  },
  declined: {
    label: "declined",
    className:
      "border-red-200 bg-red-50 text-red-700 dark:border-red-900 dark:bg-red-950/35 dark:text-red-200",
  },
};

type InstanceProps = {
  status: ContractInstanceStatus;
  className?: string;
};

export function SignInstanceStatusChip({ status, className }: InstanceProps) {
  const chip = INSTANCE_CHIP[status] ?? INSTANCE_CHIP.draft;
  return (
    <span
      title={chip.title}
      className={`rounded border px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide ${chip.className}${className ? ` ${className}` : ""}`}
    >
      {chip.label}
    </span>
  );
}

type SignerProps = {
  status: SignerStatus;
  className?: string;
};

export function SignSignerStatusChip({ status, className }: SignerProps) {
  const chip = SIGNER_CHIP[status] ?? SIGNER_CHIP.pending;
  return (
    <span
      className={`rounded border px-1.5 py-0.5 text-[10px] font-medium ${chip.className}${className ? ` ${className}` : ""}`}
    >
      {chip.label}
    </span>
  );
}
